import YoutubeSingle from './YoutubeSingle';
import { stripTrailingSlash } from '@/helpers/StringHelper';
import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import React, { FC, useMemo } from 'react';

interface PageComponentQuery {
  allPageComponent: {
    nodes: GatsbyTypes.PageComponent[];
  };
}

interface PageComponentProps {
  id: string;
  defaultValue?: string;
  displayBlock?: boolean;
  addClass?: string;
}

/**
 * 페이지관리 영역 컴포넌트
 * children으로 넘어온 값이 기본출력, db에서 조회되면 데이터를 렌더링해서 보여줌
 */
const PageComponent: FC<PageComponentProps> = React.memo(
  ({ id, children, displayBlock, addClass, }) => {
    const location = useLocation();
    const path = useMemo(() => stripTrailingSlash(location.pathname), [
      location,
    ]);
    const {
      allPageComponent: { nodes: pages },
    } = useStaticQuery<PageComponentQuery>(graphql`
      query {
        allPageComponent {
          nodes {
            path
            pathName
            componentInfos {
              id
              type
              value
            }
          }
        }
      }
    `);

    const ContainerEl = useMemo(() => (displayBlock ? `div` : `span`), [
      displayBlock,
    ]);

    const commonProps = useMemo(
      () => ({
        'data-cid': id,
      }),
      [id],
    );

    const component = useMemo(() => {
      const currentPage = pages.find((p) => p.path === path);
      if (!currentPage?.componentInfos) {
        return null;
      }
      return currentPage.componentInfos.find((c) => c && c.id === id);
    }, [id, pages, path]);

    // 넘어온 컴포넌트 데이터가 없으면 children 출력이 기본
    if (!component) {
      return <ContainerEl {...commonProps}>{children}</ContainerEl>;
    }

    if (component.type === `image`) {
      return <img {...commonProps} src={component.value} alt="이미지" />;
    }

    if (component.type === `video`) {
      // eslint-disable-next-line jsx-a11y/media-has-caption
      return (
        <video {...commonProps} src={component.value} controls playsInline />
      );
    }

    if (component.type === `youtubeId`) {
      return (
        <YoutubeSingle iframeProps={commonProps} videoId={component.value} addClass={addClass} />
      );
    }

    if (component.type === `youtubeUrl`) {
      return <YoutubeSingle iframeProps={commonProps} src={component.value} addClass={addClass} />;
    }

    if (component.type === `multiUrl`) {
      return <YoutubeSingle iframeProps={commonProps} src={component.value} addClass={addClass} />;
    }

    if (component.type === `html`) {
      return (
        <ContainerEl
          {...commonProps}
          dangerouslySetInnerHTML={{ __html: component.value || `` }}
        />
      );
    }

    return <ContainerEl {...commonProps}>{component.value}</ContainerEl>;
  },
);

export default PageComponent;
